import { PUBLIC_COMPUTER_CLAIM } from './constants';

/**
 * Whether the IdP flagged this login as public-computer. Cognito custom attributes are always
 * strings, so the claim is `"true"`/`"false"` — and `"false"` is truthy, so compare explicitly.
 * A real boolean is accepted too, so an attribute-mapping change can't silently disable it.
 */
export const isPublicComputerClaim = (claim: unknown): boolean => {
  if (typeof claim === 'boolean') {
    return claim;
  }
  // Locale-agnostic: this is a protocol value, not display text.
  return typeof claim === 'string' && claim.toLowerCase() === 'true';
};

/** The claim as carried in an id token's payload, for when the decoded profile lacks it. */
const claimFromIdToken = (idToken: unknown): unknown => {
  const payload = String(idToken ?? '').split('.')[1];
  if (!payload) {
    return undefined;
  }
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4)))[PUBLIC_COMPUTER_CLAIM];
  } catch {
    return undefined;
  }
};

/**
 * Whether a signed-in user is on a public computer, resolved the same way everywhere: the
 * decoded profile first, then the id token. Both must agree — a caller that checked only the
 * profile would classify an isolated session as ordinary and migrate its tokens back to
 * durable storage.
 */
export const isPublicComputerUser = (user: { profile?: Record<string, unknown>; id_token?: unknown }): boolean => {
  const fromProfile = user.profile?.[PUBLIC_COMPUTER_CLAIM];
  if (fromProfile !== undefined) {
    return isPublicComputerClaim(fromProfile);
  }
  return isPublicComputerClaim(claimFromIdToken(user.id_token));
};
